import { type Coordinates } from "../../../types/pixel";

const getBounds = (start: Coordinates, end: Coordinates) => ({
  minX: Math.min(start.x, end.x),
  maxX: Math.max(start.x, end.x),
  minY: Math.min(start.y, end.y),
  maxY: Math.max(start.y, end.y),
});

const getCircleGeometry = (start: Coordinates, end: Coordinates) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  return {
    centerX: start.x,
    centerY: start.y,
    radius: Math.round(Math.sqrt(dx * dx + dy * dy)),
  };
};

const dedupeCoordinates = (coordinates: Coordinates[]): Coordinates[] =>
  Array.from(new Map(coordinates.map((c) => [`${c.x},${c.y}`, c])).values());

export const getLineCoordinates = (
  start: Coordinates,
  end: Coordinates
): Coordinates[] => {
  const coordinates: Coordinates[] = [];
  let { x: x0, y: y0 } = start;
  const { x: x1, y: y1 } = end;

  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;

  while (true) {
    coordinates.push({ x: x0, y: y0 });
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }

  return coordinates;
};

export const getSquareCoordinates = (
  start: Coordinates,
  end: Coordinates
): Coordinates[] => {
  const coordinates: Coordinates[] = [];

  const { minX, maxX, minY, maxY } = getBounds(start, end);

  // Top edge
  coordinates.push(
    ...getLineCoordinates({ x: minX, y: minY }, { x: maxX, y: minY })
  );
  // Right edge
  coordinates.push(
    ...getLineCoordinates({ x: maxX, y: minY }, { x: maxX, y: maxY })
  );
  // Bottom edge
  coordinates.push(
    ...getLineCoordinates({ x: maxX, y: maxY }, { x: minX, y: maxY })
  );
  // Left edge
  coordinates.push(
    ...getLineCoordinates({ x: minX, y: maxY }, { x: minX, y: minY })
  );

  return dedupeCoordinates(coordinates);
};

export const getFilledSquareCoordinates = (
  start: Coordinates,
  end: Coordinates
): Coordinates[] => {
  const coordinates: Coordinates[] = [];

  const { minX, maxX, minY, maxY } = getBounds(start, end);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      coordinates.push({ x, y });
    }
  }

  return coordinates;
};

export const getCircleCoordinates = (
  start: Coordinates,
  end: Coordinates
): Coordinates[] => {
  const coordinates: Coordinates[] = [];

  const { centerX, centerY, radius } = getCircleGeometry(start, end);

  if (radius === 0) {
    coordinates.push({ x: centerX, y: centerY });
    return coordinates;
  }

  // Modified circle algorithm for more rounded appearance
  let x = 0;
  let y = radius;
  let radiusError = 0 - radius;

  const plotCirclePoints = (cx: number, cy: number, x: number, y: number) => {
    coordinates.push({ x: cx + x, y: cy + y });
    coordinates.push({ x: cx - x, y: cy + y });
    coordinates.push({ x: cx + x, y: cy - y });
    coordinates.push({ x: cx - x, y: cy - y });
    coordinates.push({ x: cx + y, y: cy + x });
    coordinates.push({ x: cx - y, y: cy + x });
    coordinates.push({ x: cx + y, y: cy - x });
    coordinates.push({ x: cx - y, y: cy - x });
  };

  while (x <= y) {
    plotCirclePoints(centerX, centerY, x, y);
    x++;
    if (radiusError < 0) {
      radiusError += 2 * x + 1;
    } else {
      y--;
      radiusError += 2 * (x - y) + 1;
    }
  }

  return dedupeCoordinates(coordinates);
};

export const getFilledCircleCoordinates = (
  start: Coordinates,
  end: Coordinates
): Coordinates[] => {
  const coordinates: Coordinates[] = [];

  const { centerX, centerY, radius } = getCircleGeometry(start, end);

  if (radius === 0) {
    coordinates.push({ x: centerX, y: centerY });
    return coordinates;
  }

  // Fill the disc: every cell whose center falls within the radius. The +0.5
  // bias matches the rounded look of the outline algorithm above.
  const r2 = (radius + 0.5) * (radius + 0.5);
  for (let y = centerY - radius; y <= centerY + radius; y++) {
    for (let x = centerX - radius; x <= centerX + radius; x++) {
      const ddx = x - centerX;
      const ddy = y - centerY;
      if (ddx * ddx + ddy * ddy <= r2) {
        coordinates.push({ x, y });
      }
    }
  }

  return coordinates;
};
